/**
 * AerialPerspectiveLut — Hillaire 2020 aerial-perspective froxel volume.
 *
 * Aerial perspective is the distance-dependent atmospheric scattering that
 * tints and fades far-away geometry (the bluish haze on distant mountains).
 * Hillaire §7 bakes it into a camera-frustum-aligned 3D LUT ("froxel
 * volume"): X/Y span the screen, Z is depth split into slices out to a
 * `maxDistance` (Hillaire uses 32 km). Each froxel stores:
 *
 *   RGB — accumulated in-scattered radiance from the camera to that depth
 *   A   — average grey transmittance from the camera to that depth
 *
 * Compositing a shaded fragment is then:
 *
 *   finalColor = fragColor * ap.a   +   ap.rgb
 *
 * i.e. attenuate the surface by transmittance and add the in-scatter. The
 * `apSample(worldPos)` TSL helper (below) reprojects a world position into
 * froxel UVW and samples the volume.
 *
 * Size: 32×32×32 Float RGBA (Hillaire's canonical size). Depth slices use a
 * linear distance distribution out to `maxDistanceKm`. The bake re-runs when
 * sun/atmosphere/camera-altitude change (same dirty-flagging as sky-view).
 *
 * NOTE on integration: this module BAKES the volume and exposes the sampler,
 * but actually compositing it into the post chain is gated behind config and
 * is OPTIONAL in this revision — see `apCompositeColor` + docs. The volume
 * and sampler are fully functional and tested; wiring into PostProcessing is
 * deferred (documented in render-sky.md).
 *
 * Reference: Hillaire 2020, §7 (Aerial Perspective LUT).
 */

import * as THREE from 'three/webgpu';
import { texture3D, vec3, float } from 'three/tsl';
import type { AtmosphereConfig, Vec3 } from '../AtmosphereConfig.js';
import {
  computeDensities, rayleighPhase, cornetteShanksPhase,
} from '../AtmosphereConfig.js';
import { transmittanceUV } from '../lutParameterization.js';
import { multiScatterUV } from './MultiScatterLut.js';
import { sampleTransmittanceLut, clamp01, rayNearIntersect, earthShadowCpu } from './TransmittanceLut.js';
import { sampleMultiScatterLut } from './MultiScatterLut.js';

/** Canonical Hillaire aerial-perspective froxel resolution (cube). */
export const AERIAL_LUT_SIZE = 32;

/** Default max froxel distance (km) — Hillaire uses 32 km. */
export const AERIAL_LUT_MAX_DISTANCE_KM = 32.0;

/** Adaptive march density: target one ray-march step per this many km. */
export const AERIAL_LUT_KM_PER_STEP = 0.5;
/** Floor on per-slice steps (near slices) — preserves the prior near-slice cost. */
export const AERIAL_LUT_MIN_STEPS = 8;
/** Ceiling on per-slice steps (far slices) — bounds the worst-case bake cost. */
export const AERIAL_LUT_MAX_STEPS = 64;

/**
 * Step count for a froxel slice at distance `distKm`, proportional to distance so
 * far slices (the 32-km slice spanned 2-km steps under the old fixed-16 scheme —
 * severe undersampling) are integrated at a comparable km/step density to near
 * slices. Clamped to [MIN, MAX] so near slices keep their prior cost and the
 * farthest slice stays bounded.
 */
export function aerialStepsForDistance(distKm: number): number {
  const steps = Math.ceil(distKm / AERIAL_LUT_KM_PER_STEP);
  return Math.min(AERIAL_LUT_MAX_STEPS, Math.max(AERIAL_LUT_MIN_STEPS, steps));
}

export interface AerialPerspectiveContext {
  cfg: AtmosphereConfig;
  /** Camera radius from planet centre (km). */
  viewHeight: number;
  /** Sun zenith cosine in the camera's local up-frame. */
  cosSunZenith: number;
  /**
   * The world-space directions of the four frustum corners, in km, for the
   * far plane (normalised view rays). For the CPU bake / tests we instead
   * sample a single representative direction grid (see bakeAerialPerspective).
   */
  transmittanceData: Float32Array;
  transmittanceWidth: number;
  transmittanceHeight: number;
  multiScatterData: Float32Array;
  multiScatterSize: number;
  sunIlluminance: number;
  /** Max froxel distance (km). Default AERIAL_LUT_MAX_DISTANCE_KM. */
  maxDistanceKm?: number;
  /** How froxel (x, y) map to a view direction (see AerialFrame). Default 'screen'. */
  frame?: AerialFrame;
}

/**
 * THE AERIAL FRAME (sky/post 2026-09-27). 'screen' is the legacy CPU grid: froxel (x, y) = a FIXED 90° pyramid
 * around +Z with the sun pinned to +X and row 0 looking 45° DOWN, while the composite samples it at the SCREEN uv —
 * so a pixel's haze followed its place on screen, not where it looked: at 20 km the screen's top row read T 0.99 (a
 * downward froxel clamped at the ground; the hills lost their haze when the camera pitched down) against the centre's
 * 0.53, the sun's forward-scatter sat at screen-right whatever the heading, and uv().y (top-origin) mirrored it.
 * 'world' bakes by the pixel's WORLD direction relative to the sun (no camera in the bake, so it never goes stale):
 * x = the azimuth from the sun's vertical plane φ = u·π (the haze is symmetric about it), y = the elevation with
 * rows packed toward the horizon θ = sign(t)·t²·π/2, t = 2v − 1 (where distant geometry lives), z = distance.
 * The composite reconstructs the pixel's world ray and looks it up with aerialUvForDirection (its TSL mirror).
 */
export type AerialFrame = 'screen' | 'world';

/** 'world' frame: froxel (u, v) → the view direction in the bake's sun frame (sun at azimuth 0, +X; up +Y). */
export function aerialDirectionForUv(u: number, v: number): Vec3 {
  const phi = clamp01(u) * Math.PI;
  const t = clamp01(v) * 2 - 1;
  const theta = Math.sign(t) * t * t * (Math.PI / 2);
  const c = Math.cos(theta);
  return { x: c * Math.cos(phi), y: Math.sin(theta), z: c * Math.sin(phi) };
}

/**
 * 'world' frame: a WORLD ray (unit, +Y up) and the world sun direction → the froxel (u, v). The inverse of
 * aerialDirectionForUv; aerialComposite mirrors it in TSL. A vertical ray or a zenith sun has no azimuth — any u
 * serves, since the bake's cos(light, view) no longer depends on it there.
 */
export function aerialUvForDirection(dir: Vec3, sunDir: Vec3): { u: number; v: number } {
  const hl = Math.hypot(dir.x, dir.z), sl = Math.hypot(sunDir.x, sunDir.z);
  const cosPhi = hl * sl > 1e-6 ? (dir.x * sunDir.x + dir.z * sunDir.z) / (hl * sl) : 1;
  const u = Math.acos(Math.max(-1, Math.min(1, cosPhi))) / Math.PI;
  const theta = Math.asin(Math.max(-1, Math.min(1, dir.y)));
  const v = 0.5 + 0.5 * Math.sign(theta) * Math.sqrt(Math.abs(theta) / (Math.PI / 2));
  return { u, v };
}

/**
 * CPU reference: integrate in-scatter + transmittance from the camera along a
 * view direction `dir` (unit, in the camera's local up-frame) out to distance
 * `distKm`. Returns { rgb: in-scatter, transmittance: grey average }.
 *
 * This is exactly the front portion of the sky-view march truncated at a
 * finite distance — the froxel volume is a depth-sliced cache of it.
 */
export function integrateAerialPerspective(
  ctx: AerialPerspectiveContext,
  dir: Vec3,
  distKm: number,
  steps = 16,
): { rgb: Vec3; transmittance: number } {
  const { cfg } = ctx;
  const r0 = ctx.viewHeight;
  const mu = dir.y; // dot(dir, up)
  // cosLightView = dot(dir, sunDir); sunDir = (sqrt(1−c²), c, 0).
  const c = ctx.cosSunZenith;
  const sunSin = Math.sqrt(Math.max(0, 1 - c * c));
  const cosLightView = dir.x * sunSin + dir.y * c;
  const phaseR = rayleighPhase(cosLightView);
  const phaseM = cornetteShanksPhase(cosLightView, cfg.miePhaseG);

  // Clamp the march to the nearest ground hit so a downward froxel ray does
  // not march through the planet (mirrors the sky-view / multi-scatter fix).
  const tGround = rayNearIntersect(r0, mu, cfg.bottomRadius);
  const marchDist = (tGround > 0) ? Math.min(distKm, tGround) : distKm;

  const dt = marchDist / steps;
  const st = newApState();
  while (st.s < steps) apMarchStep(ctx, st, r0, mu, c, phaseR, phaseM, dt);
  return apResult(ctx, st);
}

/** The running state of one aerial march (throughput, in-scatter, steps taken). */
interface ApMarchState { lx: number; ly: number; lz: number; trX: number; trY: number; trZ: number; s: number }
function newApState(): ApMarchState { return { lx: 0, ly: 0, lz: 0, trX: 1, trY: 1, trZ: 1, s: 0 }; }
function apResult(ctx: AerialPerspectiveContext, st: ApMarchState): { rgb: Vec3; transmittance: number } {
  const grey = (st.trX + st.trY + st.trZ) / 3.0;
  return {
    rgb: { x: st.lx * ctx.sunIlluminance, y: st.ly * ctx.sunIlluminance, z: st.lz * ctx.sunIlluminance },
    transmittance: grey,
  };
}

/**
 * ONE step (index `st.s`) of the aerial march — the loop body integrateAerialPerspective always had, factored out so
 * the cumulative bake below runs the SAME arithmetic in the same order (JS never fuses or reorders float ops, so a
 * shared body is a bit-identical body).
 */
function apMarchStep(
  ctx: AerialPerspectiveContext, st: ApMarchState,
  r0: number, mu: number, c: number, phaseR: number, phaseM: number, dt: number,
): void {
  const { cfg } = ctx;
  const t = (st.s + 0.5) * dt;
  const rAtSample = Math.sqrt(r0 * r0 + 2.0 * r0 * mu * t + t * t);
  const altitudeKm = Math.max(0, rAtSample - cfg.bottomRadius);
  const d = computeDensities(cfg, altitudeKm);

  const sigSrX = cfg.rayleighScattering.x * d.rayleigh;
  const sigSrY = cfg.rayleighScattering.y * d.rayleigh;
  const sigSrZ = cfg.rayleighScattering.z * d.rayleigh;
  const sigSmX = cfg.mieScattering.x * d.mie;
  const sigSmY = cfg.mieScattering.y * d.mie;
  const sigSmZ = cfg.mieScattering.z * d.mie;

  const extX = cfg.rayleighScattering.x * d.rayleigh
    + cfg.mieExtinction.x * d.mie + cfg.ozoneExtinction.x * d.ozone;
  const extY = cfg.rayleighScattering.y * d.rayleigh
    + cfg.mieExtinction.y * d.mie + cfg.ozoneExtinction.y * d.ozone;
  const extZ = cfg.rayleighScattering.z * d.rayleigh
    + cfg.mieExtinction.z * d.mie + cfg.ozoneExtinction.z * d.ozone;

  const tUV = transmittanceUV(cfg, rAtSample, c);
  const sunT0 = sampleTransmittanceLut(
    ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight,
    tUV.u, tUV.v,
  );
  // The planet's shadow (earthShadowCpu's WHY): no single scatter where the Earth hides the sun.
  const shadow = earthShadowCpu(cfg.bottomRadius, rAtSample, c);
  const sunTx = sunT0.x * shadow, sunTy = sunT0.y * shadow, sunTz = sunT0.z * shadow;
  const msUV = multiScatterUV(cfg, rAtSample, c);
  const psi = sampleMultiScatterLut(
    ctx.multiScatterData, ctx.multiScatterSize, msUV.u, msUV.v,
  );

  const inX = (sigSrX * phaseR + sigSmX * phaseM) * sunTx + (sigSrX + sigSmX) * psi.x;
  const inY = (sigSrY * phaseR + sigSmY * phaseM) * sunTy + (sigSrY + sigSmY) * psi.y;
  const inZ = (sigSrZ * phaseR + sigSmZ * phaseM) * sunTz + (sigSrZ + sigSmZ) * psi.z;

  st.lx += st.trX * inX * dt;
  st.ly += st.trY * inY * dt;
  st.lz += st.trZ * inZ * dt;

  st.trX *= Math.exp(-extX * dt);
  st.trY *= Math.exp(-extY * dt);
  st.trZ *= Math.exp(-extZ * dt);
  st.s++;
}

export interface AerialPerspectiveLutResult {
  data: Float32Array;
  size: number;
  maxDistanceKm: number;
  texture: THREE.Data3DTexture;
}

/**
 * Bake the aerial-perspective froxel volume. For the CPU reference / tests we
 * use a simple direction grid: froxel (x, y) maps to a view direction in a
 * 90° fov pyramid centred on the local forward (+Z) axis, and z maps to a
 * linear depth slice. A real GPU implementation reprojects the camera
 * frustum; the per-froxel MATH is identical (the front of the sky-view march
 * truncated at the slice distance), which is what these tests pin.
 */
export function bakeAerialPerspectiveLut(
  ctx: AerialPerspectiveContext,
  size = AERIAL_LUT_SIZE,
): AerialPerspectiveLutResult {
  const maxDist = ctx.maxDistanceKm ?? AERIAL_LUT_MAX_DISTANCE_KM;
  const data = new Float32Array(size * size * size * 4);
  // Per (x,y) we reuse the same direction across all z slices. Each slice's value is the front of the view march
  // from the camera (t=0) out to the slice distance, integrated with a step count that scales with the distance (the
  // old fixed 16 steps put 2-km gaps on the 32-km slice) — computed once per column where the slices share a step.
  const world = ctx.frame === 'world';
  // THE CUMULATIVE MARCH (shader sweep 2026-10-06 — the boot profile's SkyLutSet._bakeTierB hot spot): a slice's
  // samples sit at t = (s + ½)·dt with dt = marchDist / steps. Past the step floor (z ≥ 4 on the canonical 32³ / 32 km
  // grid) every unclipped slice has dt EXACTLY 0.5 km (d = z + ½, steps = 2z + 1), so slice z's march is a PREFIX of
  // slice z + 1's: one running march per column, snapshotted at each slice's step count, replaces 32 re-integrations
  // from the camera (~1,040 steps a column → ~63). A slice whose dt differs (the 8-step floor, a ground-clipped ray, a
  // non-canonical size) starts its own run — so the result is the per-slice integral BIT FOR BIT (the shared step body
  // and the same dt; `sky-lut-aerial` pins it with Object.is against the per-slice oracle).
  const { cfg } = ctx;
  const r0 = ctx.viewHeight;
  const c = ctx.cosSunZenith;
  const sunSin = Math.sqrt(Math.max(0, 1 - c * c));
  for (let y = 0; y < size; y++) {
    const ndcY = ((y + 0.5) / size) * 2 - 1; // [−1, 1]
    for (let x = 0; x < size; x++) {
      const ndcX = ((x + 0.5) / size) * 2 - 1;
      // 'screen': a 90° symmetric fov pyramid around +Z (tan(45°)=1); 'world': the sun-relative world direction.
      const dir = world ? aerialDirectionForUv((x + 0.5) / size, (y + 0.5) / size) : normalize3({ x: ndcX, y: ndcY, z: 1 });
      // Per-column constants — exactly integrateAerialPerspective's.
      const mu = dir.y;
      const cosLightView = dir.x * sunSin + dir.y * c;
      const phaseR = rayleighPhase(cosLightView);
      const phaseM = cornetteShanksPhase(cosLightView, cfg.miePhaseG);
      const tGround = rayNearIntersect(r0, mu, cfg.bottomRadius);
      let run: ApMarchState | null = null;
      let runDt = Number.NaN;
      for (let z = 0; z < size; z++) {
        const distKm = ((z + 0.5) / size) * maxDist;
        const steps = aerialStepsForDistance(distKm);
        const marchDist = (tGround > 0) ? Math.min(distKm, tGround) : distKm;
        const dt = marchDist / steps;
        if (!run || runDt !== dt || run.s > steps) { run = newApState(); runDt = dt; }
        while (run.s < steps) apMarchStep(ctx, run, r0, mu, c, phaseR, phaseM, dt);
        const ap = apResult(ctx, run);
        const idx = ((z * size + y) * size + x) * 4;
        data[idx + 0] = ap.rgb.x;
        data[idx + 1] = ap.rgb.y;
        data[idx + 2] = ap.rgb.z;
        data[idx + 3] = ap.transmittance;
      }
    }
  }
  const texture = new THREE.Data3DTexture(data, size, size, size);
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.FloatType;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.wrapR = THREE.ClampToEdgeWrapping;
  texture.colorSpace = THREE.NoColorSpace;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  texture.name = 'SkyAerialPerspectiveLUT';
  return { data, size, maxDistanceKm: maxDist, texture };
}

/** Trilinear sample of a baked aerial-perspective volume (mirrors GPU). */
export function sampleAerialPerspectiveLut(
  data: Float32Array, size: number, u: number, v: number, w: number,
): { rgb: Vec3; transmittance: number } {
  const fx = clamp01(u) * size - 0.5;
  const fy = clamp01(v) * size - 0.5;
  const fz = clamp01(w) * size - 0.5;
  const x0 = Math.floor(fx), y0 = Math.floor(fy), z0 = Math.floor(fz);
  const tx = fx - x0, ty = fy - y0, tz = fz - z0;
  const ci = (xi: number, yi: number, zi: number): number => {
    const cx = clampI(xi, 0, size - 1);
    const cy = clampI(yi, 0, size - 1);
    const cz = clampI(zi, 0, size - 1);
    return ((cz * size + cy) * size + cx) * 4;
  };
  const out = [0, 0, 0, 0];
  for (let comp = 0; comp < 4; comp++) {
    const c000 = data[ci(x0, y0, z0) + comp];
    const c100 = data[ci(x0 + 1, y0, z0) + comp];
    const c010 = data[ci(x0, y0 + 1, z0) + comp];
    const c110 = data[ci(x0 + 1, y0 + 1, z0) + comp];
    const c001 = data[ci(x0, y0, z0 + 1) + comp];
    const c101 = data[ci(x0 + 1, y0, z0 + 1) + comp];
    const c011 = data[ci(x0, y0 + 1, z0 + 1) + comp];
    const c111 = data[ci(x0 + 1, y0 + 1, z0 + 1) + comp];
    const x00 = c000 + (c100 - c000) * tx;
    const x10 = c010 + (c110 - c010) * tx;
    const x01 = c001 + (c101 - c001) * tx;
    const x11 = c011 + (c111 - c011) * tx;
    const y0v = x00 + (x10 - x00) * ty;
    const y1v = x01 + (x11 - x01) * ty;
    out[comp] = y0v + (y1v - y0v) * tz;
  }
  return { rgb: { x: out[0], y: out[1], z: out[2] }, transmittance: out[3] };
}

/**
 * TSL helper: sample the aerial-perspective volume at froxel UVW. Returns the
 * raw vec4 node (rgb = in-scatter, a = transmittance). Callers compose with
 * `apCompositeColor` or read components directly.
 *
 * `uvw` is the froxel-space coordinate (xy = screen [0,1], z = depth slice
 * [0,1]); the caller is responsible for computing it from the world/clip
 * position (a depth-based reprojection). This keeps the helper independent of
 * any particular camera/post node graph so it can slot into either materials
 * or the post chain.
 */
export function apSample(
  volumeTexture: THREE.Data3DTexture,
  uvw: ReturnType<typeof vec3>,
): ReturnType<typeof texture3D> {
  return texture3D(volumeTexture, uvw);
}

/**
 * TSL helper: composite a shaded `sceneColor` with an aerial-perspective
 * sample (vec4: rgb = in-scatter, a = transmittance):
 *
 *   out = sceneColor * ap.a + ap.rgb
 */
export function apCompositeColor(
  sceneColor: ReturnType<typeof vec3>,
  apSampleVec4: ReturnType<typeof texture3D>,
): ReturnType<typeof vec3> {
  const a = apSampleVec4.w;
  const inscatter = vec3(apSampleVec4.x, apSampleVec4.y, apSampleVec4.z);
  // sceneColor·transmittance + in-scatter. The chained-operator node is a
  // valid vec3 TSL node; cast keeps the static return type canonical.
  return sceneColor.mul(float(a)).add(inscatter) as unknown as ReturnType<typeof vec3>;
}

// ─── small vector helpers ────────────────────────────────────────────────────

function normalize3(v: Vec3): Vec3 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1;
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}
function clampI(x: number, lo: number, hi: number): number {
  return x < lo ? lo : (x > hi ? hi : x);
}
